import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Radio, Youtube, Instagram, Linkedin, Loader2, Circle } from 'lucide-react';
import { adminGetLive, adminSetLive } from '../../lib/api';

const PLATFORMS = [
  { k: 'youtube', label: 'YouTube', icon: Youtube, hint: 'Plays inline on your site' },
  { k: 'instagram', label: 'Instagram', icon: Instagram, hint: 'Shows a "Watch live" button' },
  { k: 'linkedin', label: 'LinkedIn', icon: Linkedin, hint: 'Shows a "Watch live" button' },
];

export default function LiveManager() {
  const [state, setState] = useState({ is_live: false, platform: 'youtube', url: '', title: '', save_replay: true });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGetLive()
      .then((d) => setState((s) => ({ ...s, ...d, save_replay: true })))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setState((s) => ({ ...s, [k]: e.target.value }));

  const goLive = async () => {
    if (!state.url.trim()) { toast.error('Paste your live stream link first'); return; }
    setBusy(true);
    try {
      await adminSetLive({ ...state, is_live: true });
      setState((s) => ({ ...s, is_live: true }));
      window.dispatchEvent(new Event('live-changed'));
      toast.success('You are live! The red banner is now showing across your site.');
    } catch (e) { toast.error('Could not go live'); }
    finally { setBusy(false); }
  };

  const endLive = async () => {
    setBusy(true);
    try {
      const r = await adminSetLive({ ...state, is_live: false });
      setState((s) => ({ ...s, is_live: false }));
      window.dispatchEvent(new Event('live-changed'));
      toast.success(r.replay_slug ? 'Live ended — replay saved as a video post.' : 'Live ended. Banner hidden.');
    } catch (e) { toast.error('Could not end live'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="bg-white rounded-2xl p-10 text-center text-[#5B6472] shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">Loading...</div>;

  return (
    <div data-testid="live-manager" className="bg-white rounded-2xl p-5 sm:p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
      <div className="flex items-center gap-3">
        <span className={`w-10 h-10 rounded-full flex items-center justify-center ${state.is_live ? 'bg-[#E11D2A]/12 text-[#E11D2A]' : 'bg-black/5 text-[#5B6472]'}`}><Radio size={19} /></span>
        <div>
          <h3 className="font-display font-bold text-[#0E1726] text-[20px]">Go live</h3>
          <p className="text-sm text-[#5B6472]">Broadcast on YouTube/Instagram/LinkedIn, then flip this on so your site shows a live banner.</p>
        </div>
      </div>

      {state.is_live && (
        <div data-testid="live-status" className="mt-4 flex items-center gap-2 bg-[#E11D2A]/8 text-[#E11D2A] text-sm font-semibold px-4 py-2.5 rounded-xl">
          <Circle size={10} fill="currentColor" className="animate-pulse" /> You are currently LIVE — visitors see the banner now.
        </div>
      )}

      <div className="mt-5 space-y-4">
        <div>
          <label className="text-sm font-semibold text-[#0E1726]">Platform</label>
          <div className="mt-2 grid sm:grid-cols-3 gap-3">
            {PLATFORMS.map((p) => (
              <button key={p.k} data-testid={`platform-${p.k}`} onClick={() => setState((s) => ({ ...s, platform: p.k }))}
                className={`text-left rounded-xl border p-3 transition-colors ${state.platform === p.k ? 'border-[#2E6BFF] bg-[#2E6BFF]/5' : 'border-black/10 hover:border-black/20'}`}>
                <span className="inline-flex items-center gap-2 font-semibold text-[#0E1726]"><p.icon size={17} /> {p.label}</span>
                <span className="block text-xs text-[#5B6472] mt-1">{p.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-[#0E1726]">Banner title <span className="text-[#5B6472] font-normal">(optional)</span></label>
          <input data-testid="live-title" value={state.title} onChange={set('title')} placeholder="e.g. Live AMA: Breaking into PM" className="mt-1.5 w-full border border-black/10 rounded-xl px-4 py-2.5 text-[#0E1726] focus:outline-none focus:ring-2 focus:ring-[#2E6BFF]/30" />
        </div>

        <div>
          <label className="text-sm font-semibold text-[#0E1726]">
            {state.platform === 'youtube' ? 'YouTube live link' : `${PLATFORMS.find((p) => p.k === state.platform).label} live URL`}
          </label>
          <input data-testid="live-url" value={state.url} onChange={set('url')}
            placeholder={state.platform === 'youtube' ? 'https://youtube.com/watch?v=... or /live/...' : 'https://...'}
            className="mt-1.5 w-full border border-black/10 rounded-xl px-4 py-2.5 text-[#0E1726] focus:outline-none focus:ring-2 focus:ring-[#2E6BFF]/30" />
        </div>

        {state.platform === 'youtube' && (
          <label className="flex items-center gap-2 text-sm text-[#0E1726]">
            <input data-testid="save-replay" type="checkbox" checked={state.save_replay} onChange={(e) => setState((s) => ({ ...s, save_replay: e.target.checked }))} className="w-4 h-4 accent-[#2E6BFF]" />
            When I end the live, save the YouTube replay as a video post
          </label>
        )}

        <div className="pt-2 border-t border-black/5">
          {state.is_live ? (
            <button data-testid="end-live-btn" onClick={endLive} disabled={busy} className="bg-[#0E1726] text-white font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:bg-black disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />} End live
            </button>
          ) : (
            <button data-testid="go-live-btn" onClick={goLive} disabled={busy} className="bg-[#E11D2A] text-white font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:bg-[#c4141f] disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Radio size={17} />} Go live now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
